import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, Card, CardContent, CardHeader, CardTitle, InfoBox } from '@immo/ui-kit';
import { useTranslations } from '@/i18n/use-translations';
import { startSignIn } from '@/lib/auth/session';

type Provider = 'authkit' | 'google' | 'microsoft';

export function SignInPage() {
  const t = useTranslations('pages.auth.sign-in');
  const [params] = useSearchParams();
  const [busy, setBusy] = useState<Provider | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Where a route guard wanted the user to land before it bounced them here
  // (IMM-486). Carried through the IdP round-trip inside the WorkOS `state` and
  // re-validated on the callback; absent for a plain sign-in, which lands on `/`.
  const returnTo = params.get('returnTo');

  const handle = (provider: Provider) => async () => {
    setError(null);
    setBusy(provider);

    try {
      await startSignIn(provider, returnTo !== null ? { returnTo } : {});
    } catch {
      setBusy(null);
      setError(t('error'));
    }
  };

  return (
    <main className="flex min-h-screen items-center justify-center bg-canvas">
      <div className="w-full max-w-sm">
        <Card>
          <CardHeader>
            <CardTitle>{t('title')}</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            {/* Only the pressed provider shows the spinner; the other two are
                locked out but say nothing. Disabling is a lockout and applies to
                all three, while loading is feedback about one specific request —
                showing it on three buttons when one was clicked misreports what
                is happening. Every label stays its own: the swap to t('loading')
                this replaced named the state but not the provider. */}
            <div className="flex flex-col gap-3">
              <Button
                variant="primary"
                type="button"
                className="w-full"
                onClick={handle('authkit')}
                loading={busy === 'authkit'}
                disabled={busy !== null}
              >
                {t('email-password')}
              </Button>
              <Button
                variant="secondary"
                type="button"
                className="w-full"
                onClick={handle('google')}
                loading={busy === 'google'}
                disabled={busy !== null}
              >
                {t('google')}
              </Button>
              <Button
                variant="secondary"
                type="button"
                className="w-full"
                onClick={handle('microsoft')}
                loading={busy === 'microsoft'}
                disabled={busy !== null}
              >
                {t('microsoft')}
              </Button>
            </div>
            {error !== null && <InfoBox type="danger">{error}</InfoBox>}
          </CardContent>
        </Card>
      </div>
    </main>
  );
}
